<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <!-- Page Header -->
            <div class="mb-8">
                <h1 class="text-3xl font-bold text-secondary-900 dark:text-white">My Companies</h1>
                <p class="mt-2 text-secondary-600 dark:text-secondary-400">
                    Manage and switch between your companies
                </p>
            </div>

            <!-- Companies Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                <!-- Company Card -->
                <div
                    v-for="company in companies"
                    :key="company.id"
                    class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border border-secondary-200 dark:border-secondary-700 overflow-hidden hover:shadow-md transition-shadow"
                >
                    <!-- Card Header -->
                    <div class="p-6 pb-4">
                        <div class="flex items-start justify-between mb-4">
                            <div class="flex-1">
                                <div class="flex items-center space-x-2 flex-wrap">
                                    <h3 class="text-lg font-semibold text-secondary-900 dark:text-white">
                                        {{ company.name }}
                                    </h3>

                                    <!-- Default Badge (when company is default) -->
                                    <span
                                        v-if="company.is_default"
                                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200"
                                    >
                                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                        Default
                                    </span>

                                    <!-- Set as Default Button (when company is NOT default) -->
                                    <button
                                        v-else-if="company.can_access"
                                        @click="setDefaultCompany(company.id)"
                                        :disabled="settingDefault === company.id"
                                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 hover:bg-secondary-200 text-secondary-700 dark:bg-secondary-700 dark:hover:bg-secondary-600 dark:text-secondary-300 transition-colors disabled:opacity-50"
                                    >
                                        <svg v-if="settingDefault === company.id" class="animate-spin h-3 w-3 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <svg v-else class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                        </svg>
                                        <span v-if="settingDefault === company.id">Setting...</span>
                                        <span v-else>Set as Default</span>
                                    </button>
                                </div>

                                <!-- Status Badge -->
                                <div class="mt-2">
                                    <span
                                        :class="getStatusBadgeClass(company.company_status)"
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                    >
                                        {{ getStatusLabel(company.company_status) }}
                                    </span>
                                    <span
                                        v-if="company.is_owner"
                                        class="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    >
                                        Owner
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Subscription Info -->
                        <div class="space-y-2 text-sm text-secondary-600 dark:text-secondary-400">
                            <div v-if="company.subscription" class="flex items-center justify-between">
                                <span>Plan:</span>
                                <span class="font-medium text-secondary-900 dark:text-white">
                                    {{ company.subscription.plan_name }}
                                </span>
                            </div>

                            <!-- Trial Info -->
                            <div v-if="company.trial?.days_remaining !== null" class="flex items-center justify-between">
                                <span>Trial:</span>
                                <span class="font-medium text-orange-600 dark:text-orange-400">
                                    {{ company.trial.days_remaining }} days left
                                </span>
                            </div>

                            <!-- Subscription Days Remaining -->
                            <div v-else-if="company.subscription?.days_remaining !== null && company.subscription.is_ending_soon" class="flex items-center justify-between">
                                <span>Renews in:</span>
                                <span class="font-medium text-orange-600 dark:text-orange-400">
                                    {{ company.subscription.days_remaining }} days
                                </span>
                            </div>

                            <!-- Storage (only if document upload is enabled) -->
                            <div v-if="company.storage.enabled" class="flex items-center justify-between">
                                <span>Storage:</span>
                                <span class="font-medium text-secondary-900 dark:text-white">
                                    {{ company.storage.used_gb }}GB / {{ company.storage.quota_gb }}GB
                                </span>
                            </div>

                            <!-- Last Accessed -->
                            <div v-if="company.last_accessed_at" class="flex items-center justify-between">
                                <span>Last accessed:</span>
                                <span class="font-medium text-secondary-900 dark:text-white">
                                    {{ formatDate(company.last_accessed_at) }}
                                </span>
                            </div>
                        </div>

                        <!-- Warning Message for Non-Owners -->
                        <div
                            v-if="!company.can_access && company.access_message"
                            class="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                        >
                            <p class="text-sm text-red-700 dark:text-red-300">
                                {{ company.access_message }}
                            </p>
                        </div>

                        <!-- Reactivation Notice for Owners -->
                        <div
                            v-if="company.is_owner && company.company_status === 'suspended'"
                            class="mt-4 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg"
                        >
                            <p class="text-sm text-orange-700 dark:text-orange-300">
                                Reactivate your subscription to restore access
                            </p>
                        </div>
                    </div>

                    <!-- Card Actions -->
                    <div class="px-6 py-4 bg-secondary-50 dark:bg-secondary-700/50 border-t border-secondary-200 dark:border-secondary-600">
                        <!-- Access Button -->
                        <Link
                            v-if="company.can_access && company.company_status !== 'suspended'"
                            :href="`/switch-company/${company.id}`"
                            method="post"
                            as="button"
                            class="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Access Dashboard
                        </Link>

                        <!-- Reactivate Button (Owners Only) -->
                        <Link
                            v-else-if="company.is_owner && (company.company_status === 'suspended' || company.company_status === 'cancelled')"
                            :href="`/billing/reactivate?company_id=${company.id}`"
                            class="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                            Reactivate
                        </Link>

                        <!-- Suspended (Team Member) -->
                        <div
                            v-else-if="!company.can_access"
                            class="w-full bg-secondary-200 dark:bg-secondary-600 text-secondary-500 dark:text-secondary-400 px-4 py-2 rounded-lg text-sm font-medium text-center cursor-not-allowed"
                        >
                            Access Restricted
                        </div>
                    </div>
                </div>

                <!-- Add Company Card (only if under limit) -->
                <div
                    v-if="canAddMoreCompanies"
                    class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border-2 border-dashed border-secondary-300 dark:border-secondary-600 overflow-hidden hover:border-primary-500 dark:hover:border-primary-500 transition-colors cursor-pointer"
                >
                    <Link
                        href="/company/create"
                        class="flex flex-col items-center justify-center h-full p-6 min-h-[300px]"
                    >
                        <div class="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4">
                            <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                        </div>
                        <h3 class="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                            Add Another Company
                        </h3>
                        <p class="text-sm text-secondary-600 dark:text-secondary-400 text-center">
                            Create a new company with a separate subscription
                        </p>
                    </Link>
                </div>

                <!-- Maximum Companies Reached Card (only show in SaaS mode when limit is reached) -->
                <div
                    v-else-if="hasReachedCompanyLimit"
                    class="bg-white dark:bg-secondary-800 rounded-lg shadow-sm border-2 border-dashed border-secondary-300 dark:border-secondary-600 overflow-hidden"
                >
                    <div class="flex flex-col items-center justify-center h-full p-6 min-h-[300px]">
                        <div class="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mb-4">
                            <svg class="w-8 h-8 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 class="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                            Maximum Companies Reached
                        </h3>
                        <p class="text-sm text-secondary-600 dark:text-secondary-400 text-center mb-4">
                            You have reached the maximum number of companies ({{ maxCompaniesAllowed }}) allowed under your account.
                        </p>
                        <p class="text-sm text-orange-600 dark:text-orange-400 text-center font-medium">
                            To add more companies, please contact Margin Resolve support for an enterprise setup.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="!companies || companies.length === 0" class="text-center py-12">
                <div class="w-24 h-24 bg-secondary-100 dark:bg-secondary-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg class="w-12 h-12 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                </div>
                <h3 class="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                    No Companies Yet
                </h3>
                <p class="text-secondary-600 dark:text-secondary-400 mb-6">
                    Get started by creating your first company
                </p>
                <Link
                    href="/company/create"
                    class="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors"
                >
                    Create Company
                </Link>
            </div>
        </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link, router, usePage } from '@inertiajs/vue3';
import axios from 'axios';

const page = usePage();

const props = defineProps({
    companies: {
        type: Array,
        default: () => []
    },
    ownedCompaniesCount: {
        type: Number,
        default: 0
    },
    maxCompaniesAllowed: {
        type: Number,
        default: 3
    }
});

const settingDefault = ref(null);

// Computed
const canAddMoreCompanies = computed(() => {
    // Check if company creation is enabled (deployment mode check)
    const companyCreationEnabled = page.props.deployment?.features?.companyCreationEnabled ?? true;

    // Must be under company limit AND company creation must be enabled
    return props.ownedCompaniesCount < props.maxCompaniesAllowed && companyCreationEnabled;
});

// Show "Maximum Companies Reached" card only in SaaS mode when limit is reached
const hasReachedCompanyLimit = computed(() => {
    const companyCreationEnabled = page.props.deployment?.features?.companyCreationEnabled ?? true;

    // Only show in SaaS mode (company creation enabled) AND when limit is reached
    return companyCreationEnabled && props.ownedCompaniesCount >= props.maxCompaniesAllowed;
});

// Methods
const getStatusBadgeClass = (status) => {
    const classes = {
        'active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        'suspended': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        'cancelled': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    };
    return classes[status] || classes['active'];
};

const getStatusLabel = (status) => {
    const labels = {
        'active': 'Active',
        'suspended': 'Suspended',
        'cancelled': 'Cancelled'
    };
    return labels[status] || status;
};

const formatDate = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffTime = Math.abs(now - d);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
};

const setDefaultCompany = async (companyId) => {
    try {
        settingDefault.value = companyId;

        await axios.post(`/api/my-companies/${companyId}/set-default`);

        // Reload page to update UI
        router.reload();
    } catch (error) {
        console.error('Error setting default company:', error);
        alert('Failed to set default company. Please try again.');
    } finally {
        settingDefault.value = null;
    }
};
</script>
